import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, ArrowUpRight, Activity, Zap, CheckCircle2, BarChart2 } from 'lucide-react';

const INITIAL_ASSETS = [
  {
    symbol: 'XAUUSD',
    name: 'Gold Spot / USD',
    category: 'Commodities',
    price: 4337.94,
    change24h: 0.42,
    high24h: 4362.50,
    low24h: 4323.10,
    volume: '14.2K Oz',
    sparkline: [4325, 4330, 4345, 4338, 4355, 4362, 4340, 4335, 4337.94]
  },
  {
    symbol: 'BTCUSDT',
    name: 'Bitcoin / USDT',
    category: 'Crypto',
    price: 65420.00,
    change24h: 2.15,
    high24h: 66100.00,
    low24h: 64500.00,
    volume: '28.4K BTC',
    sparkline: [64600, 64900, 65200, 64800, 65500, 65800, 65200, 65420]
  },
  {
    symbol: 'ETHUSDT',
    name: 'Ethereum / USDT',
    category: 'Crypto',
    price: 3480.50,
    change24h: 1.68,
    high24h: 3540.00,
    low24h: 3410.00,
    volume: '142.8K ETH',
    sparkline: [3420, 3440, 3490, 3460, 3510, 3540, 3470, 3480.50]
  },
  {
    symbol: 'SOLUSDT',
    name: 'Solana / USDT',
    category: 'Crypto',
    price: 154.20,
    change24h: -0.85,
    high24h: 159.40,
    low24h: 151.20,
    volume: '890K SOL',
    sparkline: [156, 158, 159, 155, 153, 152, 153.5, 154.20]
  },
  {
    symbol: 'BNBUSDT',
    name: 'BNB / USDT',
    category: 'Crypto',
    price: 585.40,
    change24h: 0.92,
    high24h: 592.00,
    low24h: 578.00,
    volume: '65K BNB',
    sparkline: [580, 582, 586, 583, 589, 591, 584, 585.40]
  },
  {
    symbol: 'EURUSD',
    name: 'Euro / US Dollar',
    category: 'Forex',
    price: 1.0850,
    change24h: 0.18,
    high24h: 1.0890,
    low24h: 1.0820,
    volume: '2.4M EUR',
    sparkline: [1.083, 1.084, 1.086, 1.085, 1.088, 1.087, 1.084, 1.085]
  },
  {
    symbol: 'GBPUSD',
    name: 'British Pound / USD',
    category: 'Forex',
    price: 1.2950,
    change24h: 0.32,
    high24h: 1.2995,
    low24h: 1.2910,
    volume: '1.8M GBP',
    sparkline: [1.292, 1.293, 1.296, 1.294, 1.298, 1.297, 1.294, 1.295]
  },
  {
    symbol: 'USDJPY',
    name: 'USD / Japanese Yen',
    category: 'Forex',
    price: 154.20,
    change24h: -0.24,
    high24h: 155.10,
    low24h: 153.80,
    volume: '3.1M USD',
    sparkline: [154.6, 154.8, 154.5, 154.9, 154.4, 154.1, 154.0, 154.2]
  }
];

export default function RealtimeMultiAssetGrid({ onSelectAsset }) {
  const [assets, setAssets] = useState(INITIAL_ASSETS);
  const [filter, setFilter] = useState('ALL');

  // Real-time live micro-ticks
  useEffect(() => {
    const interval = setInterval(() => {
      setAssets(prev => prev.map(item => {
        const delta = (Math.random() - 0.49) * (item.price * 0.0008);
        const decimals = item.price < 10 ? 4 : 2;
        const newPrice = Number((item.price + delta).toFixed(decimals));
        const newSparkline = [...item.sparkline.slice(1), newPrice];
        return {
          ...item,
          price: newPrice,
          sparkline: newSparkline
        };
      }));
    }, 1500);

    return () => clearInterval(interval);
  }, []);

  const filteredAssets = filter === 'ALL' 
    ? assets 
    : assets.filter(a => a.category.toUpperCase() === filter.toUpperCase());

  return (
    <div className="space-y-6 animate-fade-in font-sans">
      {/* Header bar */}
      <div className="glass-panel p-5 rounded-2xl border border-white/10 flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse" />
            <h2 className="text-lg font-bold text-white tracking-wide uppercase font-mono">
              Cửa sổ giá Realtime // Multi-Asset Matrix
            </h2>
          </div>
          <p className="text-xs text-slate-400 font-mono mt-1">
            Theo dõi bảng giá thời gian thực & chọn tài sản trực tiếp để tải vào AI Desk phân tích
          </p>
        </div>

        {/* Filter categories */}
        <div className="flex items-center gap-2 font-mono text-xs">
          {['ALL', 'COMMODITIES', 'CRYPTO', 'FOREX'].map(cat => (
            <button
              key={cat}
              type="button"
              onClick={() => setFilter(cat)}
              className={`px-3 py-1.5 rounded-xl font-bold transition-all cursor-pointer ${
                filter === cat
                  ? 'bg-rose-500/20 text-rose-300 border border-rose-500/50 shadow-[0_0_12px_rgba(244,63,94,0.3)]'
                  : 'bg-white/5 text-slate-400 hover:text-white border border-white/10'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Asset Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {filteredAssets.map(asset => {
          const isUp = asset.change24h >= 0;
          return (
            <div
              key={asset.symbol}
              onClick={() => onSelectAsset && onSelectAsset(asset.symbol)}
              className="glass-card p-5 rounded-2xl border border-white/10 hover:border-rose-500/50 transition-all cursor-pointer group hover:shadow-[0_0_25px_rgba(244,63,94,0.15)] flex flex-col justify-between space-y-4"
            >
              <div className="space-y-3">
                {/* Symbol & Category */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center font-mono font-bold text-white text-xs">
                      {asset.symbol.substring(0, 3)}
                    </div>
                    <div>
                      <h4 className="font-bold text-white font-mono group-hover:text-rose-400 transition-colors">
                        {asset.symbol}
                      </h4>
                      <span className="text-[11px] text-slate-400 block">{asset.name}</span>
                    </div>
                  </div>

                  <span className={`px-2.5 py-1 rounded-lg text-xs font-mono font-bold flex items-center gap-1 ${
                    isUp 
                      ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30' 
                      : 'bg-rose-500/15 text-rose-400 border border-rose-500/30'
                  }`}>
                    {isUp ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                    <span>{isUp ? '+' : ''}{asset.change24h}%</span>
                  </span>
                </div>

                {/* Price Display */}
                <div>
                  <div className="text-2xl font-bold font-mono text-white">
                    ${asset.price < 10 ? asset.price.toFixed(4) : asset.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                  <div className="flex items-center justify-between text-[11px] font-mono text-slate-400 mt-1">
                    <span>24h Cao: <strong className="text-slate-200">${asset.high24h.toLocaleString()}</strong></span>
                    <span>24h Thấp: <strong className="text-slate-200">${asset.low24h.toLocaleString()}</strong></span>
                  </div>
                </div>

                {/* Mini SVG Sparkline */}
                <div className="h-10 w-full pt-1">
                  <svg className="w-full h-full overflow-visible" viewBox="0 0 100 30" preserveAspectRatio="none">
                    {(() => {
                      const min = Math.min(...asset.sparkline);
                      const max = Math.max(...asset.sparkline);
                      const range = max - min || 1;
                      const points = asset.sparkline
                        .map((p, idx) => {
                          const x = (idx / (asset.sparkline.length - 1)) * 100;
                          const y = 30 - ((p - min) / range) * 26 - 2;
                          return `${x},${y}`;
                        })
                        .join(' ');
                      return (
                        <polyline
                          points={points}
                          fill="none"
                          stroke={isUp ? '#10b981' : '#f43f5e'}
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      );
                    })()}
                  </svg>
                </div>
              </div>

              {/* Action Button */}
              <div className="pt-3 border-t border-white/10 flex items-center justify-between text-xs font-mono text-slate-400 group-hover:text-rose-400 transition-colors">
                <span>Khối lượng: {asset.volume}</span>
                <span className="flex items-center gap-1 font-bold text-white group-hover:text-rose-400">
                  <span>Mở trên AI Desk</span>
                  <ArrowUpRight className="w-3.5 h-3.5" />
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
